<template>
  <b-row align-h="center">
    <loading :active.sync="loadingOverlayActive" :can-cancel="false" />
    <b-col :sm="cardSize.sm" :md="cardSize.md" :lg="cardSize.lg" :xl="cardSize.xl">
      <b-card class="mb-2 form-card">
        <template #header class="card-header">
          <span class="small-link">
            <a href="/ressources/entreprise/guide-inscription.mp4" target="_blank">Guide Vidéo</a>
            <!-- | <a href="/ressources/entreprise/guide.pdf" target="_blank">Guide PDF</a> -->
          </span>
          <h4 class="my-2">Pré-enregistrement Entreprise</h4>
        </template>

        <b-card-body>
          <form-wizard
            @on-complete="onComplete"
            @on-change="onChangeStep"
            title=""
            subtitle=""
            nextButtonText="Suivant"
            backButtonText="Précédent"
            finishButtonText="Demande de signature"
            stepSize="xs"
            color="#3F51B5"
            ref="formWizard"
          >
            <tab-content title="💡 Informations">
              <div class="mx-auto">
                <b-card-body>
                  <b-alert show variant="info">
                    <div>
                      Les pièces justificatives suivantes pourront vous être demandées lors de la procédure :
                    </div>
                    
                    <h6 class="mt-3 mb-2"><strong>Documents obligatoires pour tous :</strong></h6>
                    <ul class="mb-3">
                      <li>Justificatif d'identité du représentant légal ou de la personne habilitée</li>
                      <li>Justificatif d'immatriculation de la personne morale</li>
                      <li>Justificatif de l'adresse du siège social</li>
                    </ul>

                    <h6 class="mt-3 mb-2"><strong>Documents spécifiques aux établissements privés :</strong></h6>
                    <ul class="mb-3">
                      <li>Mandat de gestion pour délégation des pouvoirs</li>
                    </ul>

                    <h6 class="mt-3 mb-2"><strong>Documents spécifiques aux établissements de droit public :</strong></h6>
                    <ul class="mb-3">
                      <li>Document attestant de la capacité à engager la personne morale</li>
                    </ul>

                    <div class="mt-3">
                      <small class="text-muted">
                        <strong>Note :</strong> Les documents requis dépendront du type d'établissement que vous représentez. 
                        Vous pourrez téléverser ces pièces lors de l'étape de justificatifs ou ultérieurement lors de la signature.
                      </small>
                    </div>
                  </b-alert>
                </b-card-body>
              </div>
            </tab-content>
            <tab-content title="🏛️ Établissement" :beforeChange="validateIdentifier">
              <b-form @submit.prevent="submitSiren">
                <h5>Je représente une :</h5>
                <b-form-group class="mb-3">
                  <b-row>
                    <b-col sm="6" md="4" lg="2">
                      <b-form-radio v-model="identifierType" value="siren" required>
                        Entreprise <br />
                        <small>Sirene — Système national d'identification des entreprises</small>
                      </b-form-radio>
                    </b-col>
                    <b-col sm="6" md="4" lg="2">
                      <b-form-radio v-model="identifierType" value="rna" required>
                        Association <br />
                        <small>N° Registre national des associations</small>
                      </b-form-radio>
                    </b-col>
                    <b-col sm="6" md="4" lg="2">
                      <b-form-radio v-model="identifierType" value="rnc" required>
                        Copropriété <br />
                        <small>N° Registre national des copropriétés</small>
                      </b-form-radio>
                    </b-col>
                  </b-row>
                </b-form-group>
                <b-form-group v-if="identifierType === 'siren'" label="N° SIREN*" class="mb-3">
                  <b-form-input
                    v-model="pm.siren"
                    placeholder="SIREN (9 caractères)"
                    :state="sirenIsValid ? true : null"
                  />
                </b-form-group>
                <b-form-group v-if="identifierType === 'rna'" label="N° Registre National des Associations*" class="mb-3">
                  <b-form-input v-model="pm.rna" placeholder="RNA" />
                </b-form-group>
                <b-form-group v-if="identifierType === 'rnc'" label="N° Registre National des Copropriétés*" class="mb-3">
                  <b-form-input v-model="pm.rnc" placeholder="RNC" />
                </b-form-group>
              </b-form>
            </tab-content>
            <tab-content title="📍 Domiciliation" :beforeChange="validateDomiciliation">
              <b-form>
                <b-row>
                  <b-col sm="6">
                    <b-form-group label="Raison sociale*">
                      <b-form-input
                        v-model="pm.raisonSociale"
                        type="text"
                        placeholder="Raison sociale*"
                        :disabled="pm.idPm && !!pm.raisonSociale.length"
                        required
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col sm="6">
                    <b-form-group label="Forme juridique*">
                      <b-form-input
                        v-model="pm.formeJuridique"
                        type="text"
                        placeholder="Forme juridique*"
                        :disabled="pm.idPm && !!pm.formeJuridique.length"
                        required
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col sm="12">
                    <b-form-group label="Adresse*">
                      <b-form-input
                        v-model="pm.adresse"
                        placeholder="Adresse*"
                        :disabled="pm.idPm && !!pm.adresse.length"
                        required
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col sm="6">
                    <b-form-group label="Code postal*">
                      <b-form-input
                        v-model="pm.codePostal"
                        placeholder="Code postal*"
                        :disabled="pm.idPm && !!pm.codePostal.length"
                        required
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col sm="6">
                    <b-form-group label="Ville*">
                      <b-form-input
                        v-model="pm.ville"
                        placeholder="Ville*"
                        :disabled="pm.idPm && !!pm.ville.length"
                        required
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row v-if="succursales.length">
                  <b-card-body>
                    <b-alert show variant="info">
                      Le consentement vaudra pour l'ensemble des établissements (succursales).
                    </b-alert>
                  </b-card-body>
                </b-row>
              </b-form>
            </tab-content>
            <tab-content title="✔️ Consentements" :beforeChange="validateContact">
              <template v-if="tiers && tiers.consentements && tiers.consentements.length">
                <h3>Consentements actifs</h3>
                <ul>
                  <li v-for="(consentement, index) in tiers.consentements.filter(e => (e.statut == 'ACTIF' || e.statut == 'ATTENTE_CGU'))" :key="index">
                    {{ consentementTypeToDesc(consentement.type) }} — 
                    <b-button variant="link" @click="$bvModal.show(`acte-details-modal-${index}`)" class="p-0">
                      Actes associés <b-icon icon="info-circle"></b-icon>
                    </b-button>
                    <br />
                    <span v-if="consentement.dateDju">
                      Donné le <strong>{{ new Date(consentement.dateDju).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) }}</strong>
                    </span>

                    <b-modal
                      :id="`acte-details-modal-${index}`"
                      ref="acteDetailsModal"
                      title="Actes du consentement"
                      hide-footer
                    >
                      <div v-for="(acte, index) in consentement.actesConsentis" :key="index">
                        <h6>{{ acte.numBibliothequeCnhj }}</h6>
                        <p class="text-muted">{{ acte.themesActesFormalites }}</p>
                      </div>
                    </b-modal>
                  </li>
                </ul>
              </template>

              <h3>Consentements disponibles</h3>
              <div v-if="consentements.length === 0" class="mb-4">
                <b-alert show variant="info">
                  <h6 class="mb-2"><strong>Aucun nouveau consentement disponible</strong></h6>
                  <p class="mb-0">Tous les consentements disponibles sont déjà actifs pour cette entité. Vous ne pouvez pas procéder à une nouvelle demande de consentement.</p>
                </b-alert>
              </div>
              <b-form v-else @submit.prevent="submitContact">
                <div v-for="(consentement, index) in consentements" :key="index" class="mb-4">
                  <b-row>
                    <b-col sm="12" class="mb-2">
                    <b-form-checkbox
                      v-model="consentement.checked"
                      name="consentement-tout-acte"
                      required
                    >
                      <span class="font-weight">{{ consentement.label }}</span>
                    </b-form-checkbox>
                    </b-col>
                  </b-row>
                  <b-card v-if="consentement.checked">
                    <p class="pl-4">Service responsable du consentement :</p>
                    <b-row class="pl-4">
                      <b-col sm="12" md="6" class="mb-2">
                        <b-form-input v-model="consentement.service.nom" placeholder="Nom du service*" required />
                      </b-col>
                      <b-col sm="12" md="6" class="mb-2">
                        <b-form-input v-model="consentement.service.email" placeholder="Email du service pour notification*" type="email" required />
                      </b-col>
                    </b-row>
                    <p class="pl-4">Contact opérationnel :</p>
                    <b-row class="pl-4">
                      <b-col sm="12" md="6" class="mb-2">
                        <b-form-input v-model="consentement.contact.prenom" placeholder="Prénom*" required />
                      </b-col>
                      <b-col sm="12" md="6" class="mb-2">
                        <b-form-input v-model="consentement.contact.nom" placeholder="Nom*" required />
                      </b-col>
                      <b-col sm="12" md="6" class="mb-2">
                        <b-form-input v-model="consentement.contact.qualite" placeholder="Qualité*" required />
                      </b-col>
                      <b-col sm="12" md="6" class="mb-2">
                        <b-form-input v-model="consentement.contact.email" type="email" placeholder="Adresse email*" required />
                      </b-col>
                      <b-col sm="12" md="6" class="mb-2">
                        <b-form-input v-model="consentement.contact.telFixe" type="tel" placeholder="Téléphone Fixe" />
                      </b-col>
                      <b-col sm="12" md="6" class="mb-2">
                        <b-form-input v-model="consentement.contact.telMobile" type="tel" placeholder="Téléphone Mobile" />
                      </b-col>
                    </b-row>
                  </b-card>
                </div>
              </b-form>
            </tab-content>
            <tab-content title="👤 Signataire" :beforeChange="validateSignatory">
              <b-form @submit.prevent="submitSignatory">
                <b-row>
                  <b-col sm="12" class="mb-4">
                    <div>
                      <b-form-group label="Civilité :">
                        <b-form-radio-group
                          v-model="signataire.civilite"
                          :options="[ { text: 'M', value: 'M' }, { text: 'Mme', value: 'MME' }, { text: 'Non-binaire', value: 'NB' } ]"
                          name="civilite-radio"
                        />
                      </b-form-group>
                    </div>
                  </b-col>
                  <b-col sm="12" lg="6" class="mb-4">
                    <b-form-input v-model="signataire.prenom" placeholder="Prénom*" required />
                  </b-col>
                  <b-col sm="12" lg="6" class="mb-4">
                    <b-form-input v-model="signataire.nom" placeholder="Nom*" required />
                  </b-col>
                  <b-col sm="12" lg="6" class="mb-4">
                    <b-form-input v-model="signataire.qualite" placeholder="Qualité*" required />
                  </b-col>
                  <b-col sm="12" lg="6" class="mb-4">
                    <b-form-input v-model="signataire.email" type="email" placeholder="Adresse email*" required />
                  </b-col>
                  <b-col sm="12" lg="6" class="mb-4">
                    <b-form-input v-model="signataire.tel" type="tel" placeholder="Téléphone*" required />
                  </b-col>
                </b-row>
              </b-form>
            </tab-content>
            <tab-content title="📂 Justificatifs" :beforeChange="validateAttachments">
              <h6>
                Type d'établissement :
              </h6>
              <b-form-group class="mb-4">
                <b-row>
                  <b-col sm="6">
                    <b-form-radio v-model="etablissementType" value="prive" required>
                      Établissement privé
                    </b-form-radio>
                  </b-col>
                  <b-col sm="6">
                    <b-form-radio v-model="etablissementType" value="public" required>
                      Établissement de droit public
                    </b-form-radio>
                  </b-col>
                </b-row>
              </b-form-group>

              <h6 v-b-toggle.collapse-1>
                Documents obligatoires :
              </h6>
              <b-form @submit.prevent="submitAttachments">
                <b-row>
                  <b-col sm="12" lg="6" class="mb-2">
                    <b-form-group>
                      <div style="display: flex;justify-content: space-between;">
                        <label>Justificatif d'identité</label>
                        <b-button size="sm" variant="link" v-b-modal.modal-infos-justificatif-identite>
                          <b-icon icon="info-circle"></b-icon>
                        </b-button>
                        <b-modal id="modal-infos-justificatif-identite" title="Informations supplémentaires" hide-footer>
                          Un justificatif d'identité <strong>en cours de validité</strong> du représentant légal de la personne morale ou de la personne physique ayant reçu pouvoir d'établir la déclaration au nom et pour le compte de la personne morale.
                          <ul>
                            <li>Carte nationale d'identité, ou</li>
                            <li>Passeport, ou</li>
                            <li>Titre de séjour.</li>
                          </ul>
                        </b-modal>
                      </div>

                      <b-form-file
                        v-model="piecesJustificatives.justificatifIdentiteFile"
                        accept="image/jpeg, image/png, application/pdf"
                        placeholder="JPG, PNG, PDF - Max 2Mo"
                        drop-placeholder="Déposez un fichier ici..."
                        @input="e => handleFileUpload('justificatifIdentiteFile', e)"
                      />
                    </b-form-group>
                  </b-col>
                  
                  <!-- Établissement privé -->
                  <b-modal id="modal-infos-mandat-gestion" title="Informations supplémentaires" hide-footer>
                    Un document émanant du représentant légal ou de l'organe compétent attestant de la capacité de l'auteur de la déclaration à engager la personne morale.
                  </b-modal>
                  <b-col sm="12" lg="6" class="mb-2">
                    <b-form-group>
                      <div style="display: flex;justify-content: space-between;">
                        <label>Mandat de gestion pour délégation des pouvoirs</label>
                        <b-button size="sm" variant="link" v-b-modal.modal-infos-mandat-gestion>
                          <b-icon icon="info-circle"></b-icon>
                        </b-button>
                      </div>

                      <b-form-file
                        v-model="piecesJustificatives.mandatDelegationFile"
                        accept="image/jpeg, image/png, application/pdf"
                        placeholder="JPG, PNG, PDF - Max 2Mo"
                        drop-placeholder="Déposez un fichier ici..."
                        @input="e => handleFileUpload('mandatDelegationFile', e)"
                      />
                    </b-form-group>
                  </b-col>

                  <!-- Établissement de droit public -->
                  <b-modal id="modal-infos-document-capacite" title="Informations supplémentaires" hide-footer>
                    Un document émanant de l'organe compétent attestant de la capacité de l'auteur de la déclaration à engager la personne morale.
                  </b-modal>
                  <b-col v-if="etablissementType === 'public'" sm="12" lg="6" class="mb-2">
                    <b-form-group>
                      <div style="display: flex;justify-content: space-between;">
                        <label>Document attestant de la capacité à engager la personne morale</label>
                        <b-button size="sm" variant="link" v-b-modal.modal-infos-document-capacite>
                          <b-icon icon="info-circle"></b-icon>
                        </b-button>
                      </div>

                      <b-form-file
                        v-model="piecesJustificatives.documentCapaciteFile"
                        accept="image/jpeg, image/png, application/pdf"
                        placeholder="JPG, PNG, PDF - Max 2Mo"
                        drop-placeholder="Déposez un fichier ici..."
                        @input="e => handleFileUpload('documentCapaciteFile', e)"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col sm="12" lg="6" class="mb-2">
                    <b-form-group>
                      <div style="display: flex;justify-content: space-between;">
                        <label>Justificatif d'immatriculation</label>
                        <b-button size="sm" variant="link" v-b-modal.modal-infos-justificatif-immatriculation>
                          <b-icon icon="info-circle"></b-icon>
                        </b-button>
                        <b-modal id="modal-infos-justificatif-immatriculation" title="Informations supplémentaires" hide-footer>
                          <ul>
                            <li>
                              Pour les personnes morales immatriculées au registre du commerce et des sociétés :
                              <ul>
                                <li>Extrait Kbis ou Lbis original du registre du commerce et des sociétés de moins de trois mois</li>
                              </ul>
                            </li>
                            <li>
                              Pour les autres :
                              <ul>
                                <li>Extrait original d'immatriculation au répertoire des métiers de moins de trois mois, ou</li>
                                <li>Journal d'annonces légales de mois de deux ans mentionnant le nom du représentant légal et l'adresse du siège de la personne morale, ou</li>
                                <li>Statuts ou toute autre pièce justificative attestant de l'existence légale de la personne morale et faisant apparaître le nom de son représentant légal, l'adresse de son siège, ainsi que la preuve de la déclaration auprès des organismes compétents.</li>
                              </ul>
                            </li>
                          </ul>
                        </b-modal>
                      </div>

                      <b-form-file
                        v-model="piecesJustificatives.justificatifImmatriculationFile"
                        accept="image/jpeg, image/png, application/pdf"
                        placeholder="JPG, PNG, PDF - Max 2Mo"
                        drop-placeholder="Déposez un fichier ici..."
                        @input="e => handleFileUpload('justificatifImmatriculationFile', e)"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col sm="12" lg="6" class="mb-2">
                    <b-form-group>
                      <div style="display: flex;justify-content: space-between;">
                        <label>Justificatif de l'adresse du siège</label>
                        <b-button size="sm" variant="link" v-b-modal.modal-infos-justificatif-adresse-siege>
                          <b-icon icon="info-circle"></b-icon>
                        </b-button>
                        <b-modal id="modal-infos-justificatif-adresse-siege" title="Informations supplémentaires" hide-footer>
                          L'original ou la copie de l'un des justificatifs de l'adresse du siège de la personne morale suivants :
                          <ul>
                            <li>Titre de propriété, ou</li>
                            <li>Quittance de loyer de moins de trois mois, ou</li>
                            <li>Facture (de gaz, d'électricité, de téléphone… ) de moins de trois mois.</li>
                          </ul>
                        </b-modal>
                      </div>

                      <b-form-file
                        v-model="piecesJustificatives.justificatifAdresseSiegeFile"
                        accept="image/jpeg, image/png, application/pdf"
                        placeholder="JPG, PNG, PDF - Max 2Mo"
                        drop-placeholder="Déposez un fichier ici..."
                        @input="e => handleFileUpload('justificatifAdresseSiegeFile', e)"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-form>
            </tab-content>
            <tab-content title="📋 Récapitulatif">
              <h4>Établissement</h4>
              <b-table
                  striped
                  hover
                  :fields="[ { key: 'field' }, { key: 'value' } ]"
                  :items="etablissementTableItems"
                  thead-class="hidden_header"
              />

              <h4 class="mt-4">Consentements</h4>
              <h6 v-if="!consentements.find(e => e.checked)">Aucun nouveau consentement</h6>
              <div v-else>
                <div v-for="(consentement, index) in consentements.filter(e => e.checked)" :key="index">
                  <h6>✔️ {{ consentement.label }}</h6>
                  <b-table
                      striped
                      hover
                      :fields="[ { key: 'field' }, { key: 'value' } ]"
                      :items="[
                          { field: 'Service', value: consentement.service.nom.toUpperCase()},
                          { field: 'Email du service pour notification', value: consentement.service.email.toLowerCase() },
                          { field: 'Prénom', value: consentement.contact.prenom.toUpperCase() },
                          { field: 'Nom', value: consentement.contact.nom.toUpperCase() },
                          { field: 'Qualité', value: consentement.contact.qualite.toUpperCase() },
                          { field: 'Email', value: consentement.contact.email.toLowerCase() },
                          { field: 'Tel. Fixe', value: consentement.contact.telFixe },
                          { field: 'Tel. Mobile', value: consentement.contact.telMobile || 'Non renseigné' },
                      ]"
                      thead-class="hidden_header"
                  />
                </div>
              </div>

              <h4 class="mt-4">Signataire</h4>
              <b-table
                  striped
                  hover
                  :fields="[ { key: 'field' }, { key: 'value' } ]"
                  :items="[
                      { field: 'Civilité', value: signataire.civilite },
                      { field: 'Prénom', value: signataire.prenom.toUpperCase() },
                      { field: 'Nom', value: signataire.nom.toUpperCase() },
                      { field: 'Qualité', value: signataire.qualite.toUpperCase() },
                      { field: 'Email', value: signataire.email.toLowerCase() },
                      { field: 'Téléphone', value: signataire.tel },
                  ]"
                  thead-class="hidden_header"
              />

            </tab-content>
          </form-wizard>
          <p v-if="showFinalStepSubtext" class="text-center">
            <small>La demande de signature permet de solliciter le signataire désigné pour signature électronique du formulaire de consentement</small>
          </p>
        </b-card-body>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {FormWizard, TabContent} from 'vue-form-wizard';
import 'vue-form-wizard/dist/vue-form-wizard.min.css';
import axios from '@axios';
import useJwt from "@/auth/jwt/useJwt";
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  name: "EntreprisePreRegistration",
  components: {
    FormWizard,
    TabContent,
    Loading,
  },
  data() {
    return {
      docPreview: 'convention',
      showFinalStepSubtext: false,
      cardSize: {
        sm: 12,
        md: 12,
        lg: 12,
        xl: 10,
      },
      idTiers: null,
      tiers: null,
      demandeur: {},
      identifierType: "",
      pm: {
        id: null,
        siren: "", // Numéro SIREN, si entpreprise
        rna: "", // Numéro de Registre National des Associations, si association
        rnc: "", // Numéro de Registre National des Copropriétés, si copropriété
        syndic: false,
        raisonSociale: "",
        adresse: "",
        codePostal: "",
        ville: "",
        formeJuridique: "",
      },
      succursales: [],
      consentements: [],
      signataire: {
        // civilite: "M",
        // prenom: "Boris (SI)",
        // nom: "Flesch (SI)",
        // qualite: "Signataire",
        // email: "boris+si@deepblock.eu",
        // tel: "0667639581",
        civilite: "",
        prenom: "",
        nom: "",
        qualite: "",
        email: "",
        tel: "",
      },
      piecesJustificatives: {},
      loadingOverlayActive: false,
      etablissementType: "prive",
    };
  },
  mounted() {
    const demandeur = useJwt.getUserData();
    this.demandeur = {
      civilite: demandeur.civilite,
      prenom: demandeur.prenom,
      nom: demandeur.nom,
      email: demandeur.email,
    };
  },
  computed: {
    etablissementTableItems() {
      const items = [];
      
      // Conditionally add SIREN if available
      if (this.pm.siren) {
        items.push({
          field: 'SIREN',
          value: this.pm.siren
        });
      }
      
      // Conditionally add RNA if available
      if (this.pm.rna) {
        items.push({
          field: 'Numéro RNA',
          value: this.pm.rna
        });
      }
      
      // Conditionally add RNC if available
      if (this.pm.rnc) {
        items.push({
          field: 'Numéro RNC',
          value: this.pm.rnc
        });
      }
      
      // Add other fields
      items.push(
        {
          field: 'Forme juridique',
          value: this.pm.formeJuridique ? this.pm.formeJuridique.toUpperCase() : ''
        },
        {
          field: 'Raison sociale',
          value: this.pm.raisonSociale
        },
        {
          field: 'Adresse',
          value: this.pm.adresse ? this.pm.adresse.toUpperCase() : ''
        },
        {
          field: 'Code postal',
          value: this.pm.codePostal
        },
        {
          field: 'Ville',
          value: this.pm.ville ? this.pm.ville.toUpperCase() : ''
        }
      );
      
      return items;
    },
    sirenIsValid() {
      const siren = this.pm.siren.replace(/\s/g, '');
      return /^\d{9}$/.test(siren);
    },
  },
  methods: {
    async onComplete() {
      this.loadingOverlayActive = true;
      try {
        const conventionData = {
          type: "PM",
          idTiers: this.idTiers,
          etablissement: this.pm,
          etablissementType: this.etablissementType,
          demandeur: this.demandeur,
          consentements: this.consentements.filter(e => e.checked).map(e => ({
            type: e.type,
            service: e.service,
            contactOperationnel: e.contact,
          })),
          signataire: this.signataire,
          justificatifIdentite: this.piecesJustificatives.justificatifIdentite || null,
          justificatifImmatriculation: this.piecesJustificatives.justificatifImmatriculation || null,
          justificatifAdresseSiege: this.piecesJustificatives.justificatifAdresseSiege || null,
          dpStatuts: this.piecesJustificatives.dpStatuts || null,
          dpDeclarationEngagement: this.piecesJustificatives.dpDeclarationEngagement || null,
          mandatDelegation: this.piecesJustificatives.mandatDelegation || null,
        };

        // Ajouter les champs spécifiques selon le type d'établissement
        if (this.etablissementType === 'public') {
          conventionData.documentCapacite = this.piecesJustificatives.documentCapacite || null;
          conventionData.statutsLoi = this.piecesJustificatives.statutsLoi || null;
        }

        const { data } = await axios.post(`/conventions`, conventionData);
        this.loadingOverlayActive = false;
        if (data.id) {
          this.$toast.open({
            message: 'Merci ! Les instructions de signature ont été transmises par email au signataire',
            type: 'success',
            duration: 10000,
          })
          this.$router.push({ name: 'entrepriseLogin' })
          return true;
        } else {
          return false;
        }
      } catch (err) {
        console.error(err)
        this.loadingOverlayActive = false;
        return false;
      }
    },
    onChangeStep(prev, next) {
      if (next === 5) {
        this.showFinalStepSubtext = true;
      } else {
        this.showFinalStepSubtext = false;
      }
    },
    async validateIdentifier() {
      let message = null;

      if (!this.pm[this.identifierType]) message = `${this.identifierType.toUpperCase() || "Identifiant"} requis`;
      
      // Strip spaces from SIREN for validation and API call
      if (this.identifierType === "siren" && this.pm.siren) {
        const sirenWithoutSpaces = this.pm.siren.replace(/\s/g, '');
        if (sirenWithoutSpaces.length !== 9) {
          message = "Le SIREN doit être composé de 9 caractères";
        }
        // Update the SIREN value without spaces for consistency
        this.pm.siren = sirenWithoutSpaces;
      }

      if (message) {
        this.$toast.open({ message, type: 'error' });
        return false;
      }

      try {
        const { data } = await axios.get(`/conventions/pre-registration/lookup-pm?identifierType=${this.identifierType}&identifierValue=${this.pm[this.identifierType]}`);
        
        // Reset des consentements disponibles - TOUJOURS faire ce reset
        this.consentements = [
            {
              type: "PM",
              label: "Je donne mon consentement pour tout acte liés à l'activité de mon entreprise",
              checked: false,
              service: {
                nom: "",
                email: "",
              },
              contact: {
                prenom: "",
                nom: "",
                qualite: "",
                email: "",
                telFixe: "",
                telMobile: "",
              },
            },
            {
              type: "PMSR",
              label: "Je souhaite donner de manière distincte mon consentement à recevoir les actes de la procédure de saisie des rémunérations",
              checked: false,
              service: {
                nom: "",
                email: "",
              },
              contact: {
                prenom: "",
                nom: "",
                qualite: "",
                email: "",
                telFixe: "",
                telMobile: "",
              },
            },
          ];

        if (!Array.isArray(data) || !data.length || !data[0]) {
            this.pm.idPm = null;
            // No data found - only prevent progression for SIREN, allow manual filling for RNA/RNC
            if (this.identifierType === 'siren') {
              this.$toast.open({ 
                message: "SIREN introuvable", 
                type: 'error' 
              });
              return false;
            } else {
              // For RNA/RNC, allow manual filling of the form
              return true;
            }
        }

        // Filter out items with statut RESILIATION or DISSOLUTION
        const filteredData = data.filter(item => item.statut !== "RESILIATION" && item.statut !== "DISSOLUTION");
        console.log("filteredData", filteredData);

        const pm = filteredData.find((pm) => pm.siege) || filteredData[0];
        const succursales = filteredData.filter((e) => !e.siege && e.siret !== pm.siret && !e.dateFin);
        const hierarchie = [
          {
            parentId: null,
            immatriculationId: pm.id,
            type: null,
          },
          ...succursales.map((succursale) => ({
            parentId: pm.id,
            immatriculationId: succursale.id,
            type: "SUCCURSALE",
          })),
        ];

        console.log("hierarchie", hierarchie);

        this.pm.hierarchie = hierarchie;

        // Si tiers déjà existant dans le RNC
        const siegeItem = data.find((item) => item.siege && item.idTiers);
        const firstItemWithIdTiers = data.find((item) => item.idTiers);
        this.idTiers = siegeItem?.idTiers || firstItemWithIdTiers?.idTiers || null;
        try {
          if (this.idTiers) {
            const { data } = await axios.get(`/conventions/pre-registration/tiers/${this.idTiers}/consentements`, { showToast: false });
            console.log("tiers data -> ", data.data);
            this.tiers = data.data || null;
            if (this.tiers.consentements) {
              const typesConsentementsActifsTiers = this.tiers.consentements.filter(e => e.statut === 'ACTIF').map(e => e.type);
              console.log("typesConsentementsActifsTiers", typesConsentementsActifsTiers);

              // Filtre des consentements actifs du tiers
              this.consentements = this.consentements.filter(consentement => !typesConsentementsActifsTiers.includes(consentement.type));
            }
          }
        } catch (err) {
          console.error(err);
          this.idTiers = null;
        }

        this.pm = {
          idPm: pm.id,
          siren: pm.siren,
          raisonSociale: `${pm.raisonSociale.nom1} ${pm.raisonSociale.nom2 || ''}`,
          adresse: `${pm.adresse.ligne1} ${pm.adresse.ligne2 || ''} ${pm.adresse.ligne3 || ''}`,
          codePostal: pm.adresse.codePostal || "",
          ville: pm.adresse.ville || "",
          formeJuridique: pm.formeJuridiqueLibelle || "",
          hierarchie,
        };

        this.succursales = succursales;
        
        return true;
      } catch (err) {
        console.error(err)
        return false;
      }
    },
    submitSiren() {
    },
    validateContact() {
      // Si aucun consentement n'est disponible, empêcher la navigation
      if (this.consentements.length === 0) {
        this.$toast.open({ 
          message: "Aucun nouveau consentement disponible. Vous ne pouvez pas continuer.", 
          type: 'info' 
        });
        return false;
      }

      // Vérifier qu'au moins un consentement est sélectionné
      const hasCheckedConsentement = this.consentements.some(consentement => consentement.checked);
      if (!hasCheckedConsentement) {
        this.$toast.open({ 
          message: "Aucun consentement sélectionné", 
          type: 'error' 
        });
        return false;
      }

      let message = null;
      for (const consentement of this.consentements) {
        if (!consentement.checked) continue;
        if (!consentement.service.nom) {
          message = "Service structurel requis";
          break;
        } else if (!consentement.service.email) {
          message = "Email structurel requis";
          break;
        } else if (!consentement.contact.prenom) {
          message = "Prénom requis";
          break;
        } else if (!consentement.contact.nom) {
          message = "Nom requis";
          break;
        } else if (!consentement.contact.qualite) {
          message = "Qualité requise";
          break;
        } else if (!consentement.contact.email) {
          message = "Email requis";
          break;
        } else if (!consentement.contact.telFixe && !consentement.contact.telMobile) {
          message = "Au moins un numéro de téléphone (fixe ou mobile) est requis";
          break;
        }
      }

      if (message) {
        this.$toast.open({ message, type: 'error' });
        return false;
      } else {
        return true;
      }
    },
    async validateAttachments() {
        this.loadingOverlayActive = true;
      
        const toBase64 = file => new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => resolve(reader.result)
            reader.onerror = error => reject(error)
        })

        console.log({...this.piecesJustificatives})

        let message = null
        
        // Validation du type d'établissement
        if (!this.etablissementType) {
            message = "Type d'établissement requis"
        }

        // Les documents ne sont plus obligatoires dans la pré-inscription
        // Le signataire pourra les uploader lors de la signature

        if (message) {
            this.$toast.open({ message, type: 'error' })
            this.loadingOverlayActive = false;
            return false
        }

        try {
            if (this.piecesJustificatives.justificatifIdentiteFile) this.piecesJustificatives.justificatifIdentite = await toBase64(this.piecesJustificatives.justificatifIdentiteFile);
            
            // Conversion pour établissements privés
            if (this.piecesJustificatives.mandatDelegationFile) this.piecesJustificatives.mandatDelegation = await toBase64(this.piecesJustificatives.mandatDelegationFile);
            
            // Conversion pour établissements de droit public
            if (this.etablissementType === 'public') {
                if (this.piecesJustificatives.documentCapaciteFile) this.piecesJustificatives.documentCapacite = await toBase64(this.piecesJustificatives.documentCapaciteFile);
                if (this.piecesJustificatives.statutsLoiFile) this.piecesJustificatives.statutsLoi = await toBase64(this.piecesJustificatives.statutsLoiFile);
            }
            
            // Conversions communes
            if (this.piecesJustificatives.justificatifImmatriculationFile) this.piecesJustificatives.justificatifImmatriculation = await toBase64(this.piecesJustificatives.justificatifImmatriculationFile);
            if (this.piecesJustificatives.justificatifAdresseSiegeFile) this.piecesJustificatives.justificatifAdresseSiege = await toBase64(this.piecesJustificatives.justificatifAdresseSiegeFile);
            
            // Conversions pour documents optionnels
            if (this.piecesJustificatives.dpStatutsFile) this.piecesJustificatives.dpStatuts = await toBase64(this.piecesJustificatives.dpStatutsFile);
            if (this.piecesJustificatives.dpDeclarationEngagementFile) this.piecesJustificatives.dpDeclarationEngagement = await toBase64(this.piecesJustificatives.dpDeclarationEngagementFile);

            console.log("PJs ->", this.piecesJustificatives);
            this.loadingOverlayActive = false;
            return true
        } catch (err) {
            console.error(err)
            this.$toast.open({
                message: 'Une erreur est survenue lors du téléversement des pièces',
                type: 'error',
            })
            this.loadingOverlayActive = false;
            return false
        }
    },
    submitContact() {
      if (this.validateContact()) {
        this.$refs.formWizard.nextTab();
      }
    },
    validateSignatory() {
      let message = null;
      if (!this.signataire.civilite) message = "Civilité requise";
      else if (!this.signataire.prenom) message = "Prénom requis";
      else if (!this.signataire.nom) message = "Nom requis";
      else if (!this.signataire.qualite) message = "Qualité requise";
      else if (!this.signataire.email) message = "Email requis";
      else if (!this.signataire.tel) message = "Téléphone requis";

      if (message) {
        this.$toast.open({ message, type: 'error' });
        return false;
      } else {
        return true;
      }
    },
    submitSignatory() {
      if (this.validateSignatory()) {
        this.$refs.formWizard.nextTab();
      }
    },
    handleFileUpload(file, e) {
        console.log(e)
        if (!e) return

        if (e.size > 2 * 1000000) {
            this.$toast.open({ message: 'Taille max 2Mo', type: 'error' })
            this.piecesJustificatives[file] = null
        } else if (!['application/pdf', 'image/jpeg', 'image/png'].includes(e.type)) {
            this.$toast.open({ message: 'Fichiers PDF, JPG et PNG autorisés uniquement', type: 'error' })
            this.piecesJustificatives[file] = null
        }
    },
    consentementTypeToDesc(type) {
      switch (type) {
        case "PMCA":
          return "Consentements tiers-saisi pour établissement habilité à détenir des comptes de dépôt";
        case "PMCB":
          return "Consentements tiers-saisi pour établissement de droit public habilité à détenir des comptes de dépôt";
        case "PM":
          return "Consentement pour tout acte lié à l'activité de mon entreprise";
        case "PMSR":
          return "Consentement à recevoir les actes de saisie des rémunérations";
        default:
          return "Consentement de typologie inconnue"; 
      }
    },
    async validateDomiciliation() {
      // Si le PM n'a pas été trouvé précédemment (pas d'idPm), on le crée dans le RNC
      if (!this.pm.idPm) {
        this.loadingOverlayActive = true;
        try {
          // Appel à la route backend pour créer l'entité
          const response = await axios.post('/conventions/pre-registration/entite-immatriculation', this.pm);
          if (response.data && response.data.success && response.data.data && response.data.data._id) {
            // On stocke l'id retourné dans pm.idPm et dans la hiérarchie
            this.pm.idPm = response.data.data._id;
            this.pm.hierarchie = [
              {
                parentId: null,
                immatriculationId: this.pm.idPm,
                type: null,
              }
            ];
            this.loadingOverlayActive = false;
            return true;
          } else {
            this.$toast.open({ message: "Erreur lors de la création de l'établissement", type: 'error' });
            this.loadingOverlayActive = false;
            return false;
          }
        } catch (err) {
          this.$toast.open({ message: "Erreur lors de la création de l'établissement", type: 'error' });
          this.loadingOverlayActive = false;
          return false;
        }
      }
      // Si déjà trouvé, on laisse passer
      return true;
    },
  }
};
</script>

<style lang="scss">
.lowercase {
  text-transform: lowercase;
}

.custom-control-inline.custom-radio {
  display: inline-block;
  margin-right: 16px;

  input[type=radio] {
    margin-right: 4px !important;
  }
}

.doc-preview {
  margin-right: 12px;
  &.active {
    text-decoration: underline;
    color: #000;
    font-weight: bolder;
  }
}

.hidden_header {
  display: none;
}
</style>